import { useMemo } from 'react';
import { BUDGET, CATS, dLong, fmt, pct1, sevColors } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { schemeStatus } from '../derive';
import { useApp } from '../state';
import {
  Bar,
  CARD,
  CARD_LIFT,
  Dial,
  HINT,
  SERIF_H,
  SERIF_H_LG,
  StatTile,
  TD,
  TH,
} from '../components/ui';
import { ProgressCurve } from '../components/ProgressCurve';

/**
 * The executive's page: where the scheme stands, on one screen, in plain
 * language. Everything here is read-only and computed from approved weeks —
 * the working registers, entry forms and approval queues never appear.
 */
export function Overview() {
  const { engine, snapshot } = useApp();

  const view = useMemo(() => {
    const status = schemeStatus(engine);
    const { cats, spent, fin, t, gap } = status;

    const lastWeek = engine.appr().slice(-1)[0] ?? null;
    const drains = engine.drainTotals();
    const structs = engine.structureTotals();

    const kpis = [
      {
        label: 'Physical progress',
        value: `${pct1(cats.overall)}%`,
        sub: 'approved construction work',
        color: '#0d3b35',
      },
      {
        label: 'Financial progress',
        value: `${pct1(fin.worksPct)}%`,
        sub: `Rs ${fmt(fin.works, 1)} Mn paid on construction`,
        color: '#0d3b35',
      },
      {
        label: 'Time elapsed',
        value: `${pct1(t.pct)}%`,
        sub: `${t.remaining} days to the deadline`,
        color: '#0d3b35',
      },
      {
        label: 'Progress vs time',
        value: `${gap >= 0 ? '+' : ''}${pct1(gap)}`,
        sub: 'percentage points',
        color: status.color,
      },
      {
        label: 'Drain length completed',
        value: `${fmt(drains.done)} ft`,
        sub: `of ${fmt(drains.total)} ft in scope`,
        color: '#0d3b35',
      },
      {
        label: 'Units handed over',
        value: String(structs.done),
        sub: `of ${structs.total} structures & installations`,
        color: '#0d3b35',
      },
    ];

    const catRows = CATS.map((c) => ({
      name: c.n,
      weight: `${Math.round(c.w * 1000) / 10}%`,
      pct: cats[c.k],
      budget: fmt(c.budget, 1),
      spent: fmt(spent[c.k] || 0, 1),
      spentPct: pct1(((spent[c.k] || 0) / c.budget) * 100),
    }));

    const lagging = engine
      .laggingComponents()
      .slice()
      .sort((a, b) => b.gap - a.gap)
      .slice(0, 6);

    const open = (snapshot.issues ?? []).filter((i) => i.status !== 'Resolved');
    const keyIssues = [
      ...open.filter((i) => i.severity === 'Critical'),
      ...open.filter((i) => i.severity === 'Major'),
    ].slice(0, 6);

    return { status, cats, spent, fin, t, lastWeek, kpis, catRows, lagging, keyIssues, openCount: open.length };
  }, [engine, snapshot]);

  return (
    <div
      style={sx(
        'max-width:1180px;margin:0 auto;padding:24px 16px 60px;display:flex;flex-direction:column;gap:18px',
      )}
    >
      <div style={sx('display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap')}>
        <div>
          <div style={sx(SERIF_H_LG)}>Executive Project Overview</div>
          <div style={sx('font-size:13px;color:#5a6b68;line-height:1.5;max-width:760px')}>
            Where the scheme stands today. Every figure counts only site returns approved by the
            PMU.
          </div>
        </div>
        <div style={sx('font-size:12px;color:#5a6b68;text-align:right')}>
          {view.lastWeek
            ? `Latest approved return: Week ${view.lastWeek.no}, ended ${dLong(view.lastWeek.end)}`
            : 'No weekly return has been approved yet.'}
        </div>
      </div>

      <div
        style={sxc(
          'border-radius:8px;padding:14px 16px;display:flex;gap:12px;align-items:flex-start',
          `background:${view.status.bg}`,
          `border:1px solid ${view.status.border}`,
        )}
      >
        <div
          style={sxc('width:6px;align-self:stretch;border-radius:99px;flex:none', `background:${view.status.color}`)}
        />
        <div style={sx('display:flex;flex-direction:column;gap:4px')}>
          <div style={sxc('font-size:14px;font-weight:600', `color:${view.status.color}`)}>
            {view.status.head}
          </div>
          <div style={sx('font-size:13px;color:#33403d;line-height:1.55;max-width:820px')}>
            {view.status.text}
          </div>
        </div>
      </div>

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px')}>
        {view.kpis.map((k) => (
          <StatTile key={k.label} tile={k} className="pms-lift" />
        ))}
      </div>

      <div
        style={sx(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:18px;align-items:start',
        )}
      >
        <div className="pms-lift-card" style={sx(CARD_LIFT)}>
          <div style={sxc(SERIF_H, 'margin-bottom:2px')}>The three dials</div>
          <div style={sxc(HINT, 'margin-bottom:14px')}>Work, money and time on the same scale.</div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:12px')}>
            <Dial label="Work done" value={view.cats.overall} color="#0f5c52" />
            <Dial label="Money on works" value={view.fin.worksPct} color="#c9a227" />
            <Dial label="Time used" value={view.t.pct} color="#4b8ea8" />
          </div>
          <div
            style={sx(
              'margin-top:16px;padding-top:14px;border-top:1px solid #eef2f1;display:flex;flex-direction:column;gap:8px',
            )}
          >
            <MoneyRow label="Sanctioned cost" value={fmt(BUDGET, 1)} />
            <MoneyRow label="Expenditure to date" value={fmt(view.spent.total, 1)} />
            <MoneyRow label="· on construction work" value={fmt(view.fin.works, 1)} />
            <MoneyRow label="· on land, environment, contingencies & tax" value={fmt(view.spent.misc || 0, 1)} />
            <MoneyRow label="Balance available" value={fmt(BUDGET - view.spent.total, 1)} />
            <Bar pct={(view.spent.total / BUDGET) * 100} color="#c9a227" />
          </div>
        </div>

        <div className="pms-lift-card" style={sx(CARD_LIFT)}>
          <div style={sxc(SERIF_H, 'margin-bottom:2px')}>Progress curve</div>
          <div style={sxc(HINT, 'margin-bottom:14px')}>
            Physical work done, money released and time consumed, week by week.
          </div>
          <ProgressCurve engine={engine} />
        </div>
      </div>

      <div style={sx(CARD)}>
        <div style={sxc(SERIF_H, 'margin-bottom:4px')}>Progress by work category</div>
        <div style={sxc(HINT, 'margin-bottom:16px')}>
          Weighted as per the approved cost split of the sanctioned scheme.
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px')}>
          {view.catRows.map((c) => (
            <div key={c.name} style={sx('display:flex;flex-direction:column;gap:6px')}>
              <div
                style={sx(
                  'display:flex;justify-content:space-between;align-items:baseline;gap:8px;flex-wrap:wrap',
                )}
              >
                <div style={sx('font-size:13px;font-weight:600')}>{c.name}</div>
                <div style={sx('font-size:11px;color:#5a6b68')}>weight {c.weight}</div>
                <div style={sx('flex:1')} />
                <div style={sx("font-family:'IBM Plex Mono',monospace;font-size:15px")}>
                  {pct1(c.pct)}%
                </div>
              </div>
              <Bar pct={c.pct} />
              <div style={sx('font-size:12px;color:#5a6b68')}>
                Budget Rs {c.budget} Mn &nbsp;·&nbsp; Spent Rs {c.spent} Mn ({c.spentPct}%)
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={sx(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:18px;align-items:start',
        )}
      >
        <div style={sx(CARD)}>
          <div style={sxc(SERIF_H, 'margin-bottom:4px')}>Components running behind time</div>
          <div style={sxc(HINT, 'margin-bottom:12px')}>
            Work packages whose completion trails the {pct1(view.t.pct)}% of the calendar already
            consumed.
          </div>
          {view.lagging.length ? (
            <div style={sx('overflow-x:auto')}>
              <table style={sx('width:100%;border-collapse:collapse;min-width:420px')}>
                <thead>
                  <tr>
                    {['Component', 'Done', 'Behind by'].map((h) => (
                      <th key={h} style={sx(TH)}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {view.lagging.map((r) => (
                    <tr key={r.name}>
                      <td style={sx(TD)}>{r.name}</td>
                      <td style={sxc(TD, "font-family:'IBM Plex Mono',monospace;white-space:nowrap")}>
                        {pct1(r.pct)}%
                      </td>
                      <td style={sxc(TD, 'color:#a3372c;white-space:nowrap')}>
                        {pct1(r.gap)} pts
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={sx('font-size:13px;color:#2f7d51;padding:8px 0')}>
              Every component is at or ahead of the time elapsed.
            </div>
          )}
        </div>

        <div style={sx(CARD)}>
          <div style={sxc(SERIF_H, 'margin-bottom:4px')}>Key issues on the ground</div>
          <div style={sxc(HINT, 'margin-bottom:12px')}>
            The most serious of the {view.openCount} open item(s) on the bottlenecks register.
          </div>
          {view.keyIssues.length ? (
            <div style={sx('overflow-x:auto')}>
              <table style={sx('width:100%;border-collapse:collapse;min-width:420px')}>
                <thead>
                  <tr>
                    {['Issue', 'Owner', 'Severity'].map((h) => (
                      <th key={h} style={sx(TH)}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {view.keyIssues.map((i) => {
                    const c = sevColors(i.severity);
                    return (
                      <tr key={i.id}>
                        <td style={sx(TD)}>{i.title || 'Untitled issue'}</td>
                        <td style={sxc(TD, 'color:#5a6b68')}>{i.owner || '—'}</td>
                        <td style={sx('padding:9px 10px;border-bottom:1px solid #eef2f1;font-size:12px')}>
                          <span
                            style={sxc(
                              'padding:3px 8px;border-radius:99px;font-weight:600;white-space:nowrap',
                              `background:${c.bg}`,
                              `color:${c.fg}`,
                            )}
                          >
                            {i.severity || 'Minor'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={sx('font-size:13px;color:#2f7d51;padding:8px 0')}>
              No critical or major issue is open at present.
            </div>
          )}
        </div>
      </div>

      <div
        style={sx(
          'background:#f2f6f5;border:1px solid #dbe3e0;border-radius:8px;padding:12px 16px;font-size:12.5px;color:#5a6b68;line-height:1.6',
        )}
      >
        Progress is measured as in the approved project tracker: civil work carries 51.6%,
        electrical 25.5% and mechanical 22.9% of the whole; drains count by length completed and
        structures by construction stage. Printable briefings — the Executive Summary among them —
        are under the Reports tab.
      </div>
    </div>
  );
}

function MoneyRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={sx('display:flex;justify-content:space-between;font-size:13px;gap:10px')}>
      <span style={sx('color:#5a6b68')}>{label}</span>
      <span style={sx("font-family:'IBM Plex Mono',monospace;white-space:nowrap")}>Rs {value} Mn</span>
    </div>
  );
}
